// ============================================================================
// Dashboard view-model — the shapes the dashboard components render.
// ----------------------------------------------------------------------------
// These mirror the original mock view-types so the locked Pulse dashboard UI is
// unchanged; only the SOURCE flips from mock to the real API (see
// useDashboardData). Session "type" colour mapping tolerates the open-ended
// backend `type` string by falling back to "mixed".
// ============================================================================
/** Pulse session-type colour buckets (drives SESSION_STYLE). */
export type SessionType = "strength" | "cardio" | "mobility" | "hiit" | "mixed";

export type Trend = "up" | "down" | "flat";
export type SessionStatus = "planned" | "completed";
/** Attendance lifecycle (NULL backend column normalizes to "scheduled"). */
export type AttendanceStatus =
  | "scheduled"
  | "attended"
  | "no_show"
  | "cancelled";

/** A client as the roster/snapshot render it (derived from API rows). */
export interface ClientVM {
  id: number;
  name: string;
  starred: boolean;
  occupation?: string;
  /** Latest weight from the metrics series, if any. */
  currentWeightKg?: number;
  trend: Trend;
  trendLabel: string;
  /** 0–100 goal-completion (omitted until goals wiring lands). */
  goalProgress?: number;
}

/** A session joined to its client name — what calendar/list views render. */
export interface SessionVM {
  id: number;
  clientId: number;
  clientName: string;
  status: SessionStatus;
  /** ISO-8601 local datetime "YYYY-MM-DDTHH:mm" (from performed/scheduled_at). */
  startsAt: string;
  durationMin: number;
  type: SessionType;
  /** The raw backend `type` string (preserved so inline edit round-trips it). */
  rawType?: string;
  location?: string;
  /** Saved location id when the session is pinned to a saved place. */
  locationId?: number;
  feeAmount?: number;
  /** Bare ISO currency code stored on the session (drives fee symbol). */
  currency?: string;
  paid?: boolean;
  /** Overall session RPE 1–10, when logged. */
  sessionRpe?: number;
  notes?: string;
  /** Attendance lifecycle (defaults to "scheduled" when unset). */
  attendance: AttendanceStatus;
  /** ISO-8601 UTC instant the client checked in, if attended. */
  checkedInAt?: string;
}

/** Normalize the backend attendance_status (NULL → "scheduled") to a VM value. */
export function toAttendance(
  raw: string | null | undefined,
): AttendanceStatus {
  switch (raw) {
    case "attended":
    case "no_show":
    case "cancelled":
      return raw;
    default:
      return "scheduled";
  }
}

// ----------------------------------------------------------------------------
// Activities (provenance-tagged exercises) — the calendar merge layer.
// ----------------------------------------------------------------------------

/**
 * Provenance of a calendar entry, driving its colour (see ENTRY_STYLE):
 *   pt_session         — a paid PT session (keeps its TYPE colour, not this)
 *   activity_suggested — trainer-prescribed homework (violet/ember)
 *   activity_own       — client's own self-logged exercise (lime)
 *   meal               — a day's meal summary marker (amber, total kcal)
 *   weekly_goal        — a week-level coach goal banner (lime accent, spec §1)
 */
export type EntryProvenance =
  | "pt_session"
  | "activity_suggested"
  | "activity_own"
  | "meal"
  | "weekly_goal";

/**
 * An activity as the calendar/detail render it (derived from an Activity row).
 * `kind` is the provenance, distinguishing it from a SessionVM on the merged
 * grid. `startsAt` is the device-local wall-clock string (see toLocalDateTime),
 * so the calendar buckets it by the same local-day prefix as sessions.
 */
export interface ActivityVM {
  id: number;
  clientId: number;
  clientName: string;
  /** Provenance — 'activity_own' (lime) | 'activity_suggested' (violet). */
  kind: Extract<EntryProvenance, "activity_own" | "activity_suggested">;
  source: "own" | "suggested";
  status: "planned" | "completed";
  /** ISO-8601 local datetime "YYYY-MM-DDTHH:mm" (from performed/scheduled_at). */
  startsAt: string;
  /** Display title — the free-text title (catalog name resolves at render). */
  title?: string;
  /** Open-ended backend `type` string, bucketed for colour where shown. */
  type?: string;
  exerciseId?: number;
  sets?: number;
  reps?: number;
  weightKg?: number;
  durationMin?: number;
  distance?: number;
  notes?: string;
}

export interface DashboardKpisVM {
  activeClients: number;
  sessionsThisWeek: number;
  hoursThisWeek: number;
  dueToSchedule: number;
  deltas: {
    activeClients: string;
    sessionsThisWeek: string;
    hoursThisWeek: string;
    dueToSchedule: string;
  };
}

export interface RosterEntryVM {
  client: ClientVM;
  lastSession?: SessionVM;
  daysSinceLast?: number;
  needsFollowUp: boolean;
}

/** Map the open-ended backend session `type` string to a Pulse colour bucket. */
const TYPE_BUCKETS: Record<string, SessionType> = {
  strength: "strength",
  cardio: "cardio",
  mobility: "mobility",
  hiit: "hiit",
  mixed: "mixed",
};

export function toSessionType(raw: string | null | undefined): SessionType {
  if (!raw) return "mixed";
  return TYPE_BUCKETS[raw.toLowerCase()] ?? "mixed";
}

/**
 * Convert a STORED (timezone-aware UTC) ISO timestamp into the DEVICE-LOCAL
 * "YYYY-MM-DDTHH:mm" wall-clock string the calendar's day helpers key on.
 *
 * Timezone convention: timestamps are stored UTC; the SPA displays in the
 * device's local timezone. The calendar buckets sessions by the leading local
 * date substring (and sorts lexically), so this MUST emit local wall-clock —
 * NOT the raw UTC hour. For a HK trainer (UTC+8) a "22:00 UTC" session becomes
 * "06:00" the next local day, landing on the correct calendar cell. (The old
 * implementation regex-stripped the offset and kept the raw UTC hour, which is
 * exactly the wrong-day / wrong-time bug this fixes.)
 *
 * A pure date-only value (no "T") is treated as a local midnight; a value that
 * is already plain local wall-clock (no offset / no "Z") is preserved as-is.
 */
export function toLocalDateTime(ts: string | null | undefined): string {
  if (!ts) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  // Date only -> local midnight on that calendar day.
  const dateOnly = ts.match(/^(\d{4}-\d{2}-\d{2})$/);
  if (dateOnly) return `${dateOnly[1]}T00:00`;
  // Plain local wall-clock already (no timezone offset, no trailing Z): keep
  // the date+minutes prefix verbatim so we don't shift a non-UTC literal.
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(ts);
  if (!hasZone) {
    const m = ts.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
    if (m) return `${m[1]}T${m[2]}`;
  }
  // Zoned (UTC) timestamp: parse and re-emit in the device's LOCAL time.
  const parsed = new Date(ts);
  if (Number.isNaN(parsed.getTime())) return ts;
  return `${parsed.getFullYear()}-${p(parsed.getMonth() + 1)}-${p(parsed.getDate())}T${p(parsed.getHours())}:${p(parsed.getMinutes())}`;
}

/**
 * The subset of an Activity row the VM needs (snake_case from the API). Kept
 * structural so both the trainer calendar feed (CalendarActivity, with
 * client_name) and the client calendar-entries union (Activity, no client_name)
 * map through the same converter.
 */
interface ActivityRowLike {
  id: number;
  client_id: number;
  client_name?: string;
  source: string;
  status: string;
  scheduled_at: string | null;
  performed_at: string | null;
  title: string | null;
  type: string | null;
  exercise_id: number | null;
  sets: number | null;
  reps: number | null;
  weight_kg: number | null;
  duration_min: number | null;
  distance: number | null;
  notes: string | null;
}

// ----------------------------------------------------------------------------
// Meal markers — the per-DAY amber summary the calendar renders (one marker per
// calendar day, carrying that day's total kcal), NOT one chip per meal.
// ----------------------------------------------------------------------------

/**
 * A day's meal summary as the calendar/detail render it. `kind` is always
 * `"meal"` (amber via ENTRY_STYLE). `day` is the local 'YYYY-MM-DD' the marker
 * buckets onto; `startsAt` is `${day}T00:00` so it sorts/keys exactly like the
 * other entries' device-local wall-clock strings. `clientName` is present only
 * on the trainer all-clients feed (the client's own marker has none).
 */
export interface MealMarkerVM {
  /** Stable per-day key (no DB id — markers are aggregates). */
  id: string;
  kind: "meal";
  /** Local calendar day 'YYYY-MM-DD'. */
  day: string;
  /** `${day}T00:00` — local wall-clock so it buckets/sorts with sessions. */
  startsAt: string;
  /** That day's total calories across the day's meals. */
  kcal: number;
  /** How many meals rolled into this marker (client union only; 0 if unknown). */
  count: number;
  /** Owning client id (trainer feed only). */
  clientId?: number;
  /** Owning client name (trainer all-clients feed only). */
  clientName?: string;
}

/** A trainer all-clients meal-calendar marker row (GET /api/meals/calendar). */
interface MealMarkerRowLike {
  client_id: number;
  client_name?: string;
  day: string;
  kcal: number | null;
}

/** A client-union meal marker row (kind='meal' from /me/calendar-entries). */
interface ClientMealMarkerLike {
  day: string;
  kcal: number | null;
  count?: number | null;
}

/**
 * Convert a TRAINER meal-calendar marker (one per client+day) into a
 * MealMarkerVM. The `id` keys on client+day so React lists stay stable and two
 * clients' same-day markers never collide.
 */
export function mealMarkerToVM(m: MealMarkerRowLike): MealMarkerVM {
  const day = (m.day || "").slice(0, 10);
  return {
    id: `meal-${m.client_id}-${day}`,
    kind: "meal",
    day,
    startsAt: day ? `${day}T00:00` : "",
    kcal: Number(m.kcal ?? 0),
    count: 0,
    clientId: m.client_id,
    clientName: m.client_name ?? undefined,
  };
}

/**
 * Convert a CLIENT-union meal marker (kind='meal', one per day, no client) into
 * a MealMarkerVM. Carries the day's meal `count` (the trainer feed doesn't).
 */
export function clientMealMarkerToVM(m: ClientMealMarkerLike): MealMarkerVM {
  const day = (m.day || "").slice(0, 10);
  return {
    id: `meal-${day}`,
    kind: "meal",
    day,
    startsAt: day ? `${day}T00:00` : "",
    kcal: Number(m.kcal ?? 0),
    count: Number(m.count ?? 0),
  };
}

/**
 * Convert an activity_log row into an ActivityVM. `startsAt` keys off
 * performed_at (done) else scheduled_at (planned), re-emitted as device-local
 * wall-clock so it buckets onto the same calendar cell as sessions. Provenance
 * (`kind`) is derived from the row's `source`.
 */
export function activityToVM(a: ActivityRowLike): ActivityVM {
  const startsAt = toLocalDateTime(a.performed_at ?? a.scheduled_at);
  const source: "own" | "suggested" = a.source === "own" ? "own" : "suggested";
  return {
    id: a.id,
    clientId: a.client_id,
    clientName: a.client_name ?? "",
    kind: source === "own" ? "activity_own" : "activity_suggested",
    source,
    status: a.status === "completed" ? "completed" : "planned",
    startsAt,
    title: a.title ?? undefined,
    type: a.type ?? undefined,
    exerciseId: a.exercise_id ?? undefined,
    sets: a.sets ?? undefined,
    reps: a.reps ?? undefined,
    weightKg: a.weight_kg ?? undefined,
    durationMin: a.duration_min ?? undefined,
    distance: a.distance ?? undefined,
    notes: a.notes ?? undefined,
  };
}

// ----------------------------------------------------------------------------
// Weekly-goal banners — the WEEK-LEVEL lime banner the calendar renders over a
// whole week (spec §1), keyed on the Monday `week_start`.
// ----------------------------------------------------------------------------

/**
 * A week-level weekly-goal banner as the calendars render it. `weekStart` is the
 * Monday 'YYYY-MM-DD' the banner spans; `clientName` is present only on the
 * trainer all-clients feed (the client's own banner is unattributed). The UI
 * renders LABELS from i18n keys — never the goal's free-text notes.
 */
export interface WeeklyGoalBannerVM {
  /** Stable key — the Monday `week_start` (+ client on the trainer feed). */
  id: string;
  kind: "weekly_goal";
  /** Monday 'YYYY-MM-DD' the banner spans. */
  weekStart: string;
  /** Owning client id (trainer feed only). */
  clientId?: number;
  /** Owning client name (trainer all-clients feed only). */
  clientName?: string;
  /** How many exercise-homework targets the goal carries (for a count chip). */
  exerciseTargetCount: number;
}

/** A weekly-goal banner row (trainer feed OR client union), structurally loose. */
interface WeeklyGoalBannerRowLike {
  client_id?: number;
  client_name?: string;
  week_start: string;
  exercise_targets?: { type: string; count: number }[] | null;
}

/**
 * Convert a weekly-goal banner row into a WeeklyGoalBannerVM. The `id` keys on
 * client+week (trainer feed) or just week (client union) so React lists stay
 * stable and two clients' same-week banners never collide.
 */
export function weeklyGoalBannerToVM(g: WeeklyGoalBannerRowLike): WeeklyGoalBannerVM {
  const weekStart = (g.week_start || "").slice(0, 10);
  const targets = Array.isArray(g.exercise_targets) ? g.exercise_targets : [];
  return {
    id: g.client_id != null ? `wg-${g.client_id}-${weekStart}` : `wg-${weekStart}`,
    kind: "weekly_goal",
    weekStart,
    clientId: g.client_id ?? undefined,
    clientName: g.client_name ?? undefined,
    exerciseTargetCount: targets.length,
  };
}
